import React from "react";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import type { FrontendErrorsSummary } from "@/features/observability/types/frontend-errors.types";

type Props = {
    summary: FrontendErrorsSummary;
    lowerBound: boolean;
};

type TagProps = { label: string; tone: "exact" | "min" | "approx" | "estimate" };

const Tag = ({ label, tone }: TagProps) => <span className={`fe-tag fe-tag--${tone}`}>{label}</span>;

export function FrontendErrorsKpis({ summary, lowerBound }: Props) {
    const suffix = lowerBound ? "mín." : "";
    const loss = summary.clientLoss;
    const total = frontendErrorsFormat.lossTotal(loss);
    const chips = summary.byCategory === null ? [] : summary.byCategory.buckets.filter((bucket) => bucket.count > 0);

    return (
        <div className="fe-kpis">
            <div className="fe-kpi fe-kpi--wide" data-testid="fe-kpi-total">
                <div className="fe-kpi__label">
                    Errores en el periodo
                    {lowerBound ? <Tag label="Mínimo" tone="min" /> : <Tag label="Exacto" tone="exact" />}
                </div>
                <div className="fe-kpi__value">
                    <span data-testid="fe-kpi-value" className="fe-kpi__num">{frontendErrorsFormat.count(summary.total)}</span>
                    <span className="fe-kpi__unit">{`${suffix} ocurrencias`.trim()}</span>
                </div>
                {chips.length > 0 ? (
                    <div className="fe-chips">
                        {chips.map((bucket) => {
                            const style = frontendErrorsFormat.categoryStyle(bucket.key);
                            return (
                                <span key={bucket.key} className="fe-chip">
                                    <span className="fe-swatch" style={{ background: style.color }} />
                                    {style.label}
                                    <b>{frontendErrorsFormat.count(bucket.count)}</b>
                                </span>
                            );
                        })}
                    </div>
                ) : null}
            </div>

            <div className="fe-kpi" data-testid="fe-kpi-devices">
                <div className="fe-kpi__label">
                    Dispositivos afectados
                    <Tag label="Aproximado" tone="approx" />
                </div>
                <div className="fe-kpi__value">
                    <span data-testid="fe-kpi-value" className="fe-kpi__num">{frontendErrorsFormat.count(summary.affectedDevicesApprox)}</span>
                    <span className="fe-kpi__unit">{suffix}</span>
                </div>
                <div className="fe-kpi__note">deviceId distintos · uno por instalación, no por persona</div>
            </div>

            <div className="fe-kpi" data-testid="fe-kpi-fingerprints">
                <div className="fe-kpi__label">
                    Fingerprints distintos
                    <Tag label="Aproximado" tone="approx" />
                </div>
                <div className="fe-kpi__value">
                    <span data-testid="fe-kpi-value" className="fe-kpi__num">{frontendErrorsFormat.count(summary.distinctFingerprints)}</span>
                    <span className="fe-kpi__unit">{suffix}</span>
                </div>
                <div className="fe-kpi__note">tipos de fallo diferentes</div>
            </div>

            <div className="fe-kpi" data-testid="fe-kpi-loss">
                <div className="fe-kpi__label">
                    <span className="fe-kpi__title">
                        Pérdida del cliente
                        <span
                            className="material-symbols-outlined fe-info"
                            title="Puede quedarse corta o pasarse; no es un mínimo ni un máximo."
                            aria-label="Puede quedarse corta o pasarse; no es un mínimo ni un máximo."
                        >
                            info
                        </span>
                    </span>
                    <Tag label="Estimación" tone="estimate" />
                </div>
                <div className="fe-kpi__value">
                    <span data-testid="fe-kpi-value" className="fe-kpi__num">{total === null ? "—" : `≈ ${frontendErrorsFormat.count(total)}`}</span>
                    <span className="fe-kpi__unit">no entregados</span>
                </div>
                <div className="fe-kpi__split">
                    <span>overflow <b>{frontendErrorsFormat.count(loss === null ? null : loss.overflow)}</b></span>
                    <span>expired <b>{frontendErrorsFormat.count(loss === null ? null : loss.expired)}</b></span>
                    <span>dropped <b>{frontendErrorsFormat.count(loss === null ? null : loss.dropped)}</b></span>
                </div>
            </div>

            <div className="fe-kpi fe-kpi--aside" data-testid="fe-kpi-suppressed">
                <div className="fe-kpi__label">
                    <span className="fe-kpi__title">
                        Consecuencias suprimidas
                        <span
                            className="material-symbols-outlined fe-info"
                            title="Líneas de consola y avisos descartados por llegar dentro de 1,5 s de un fallo real. No se suman a la pérdida ni a los errores."
                            aria-label="Líneas de consola y avisos descartados por llegar dentro de 1,5 s de un fallo real. No se suman a la pérdida ni a los errores."
                        >
                            info
                        </span>
                    </span>
                </div>
                <div className="fe-kpi__value">
                    <span data-testid="fe-kpi-value" className="fe-kpi__num fe-kpi__num--muted">{frontendErrorsFormat.count(summary.consequencesSuppressed)}</span>
                    <span className="fe-kpi__unit">{suffix}</span>
                </div>
                <div className="fe-kpi__note">descartadas a &lt; 1,5 s de un fallo · aparte, no es pérdida</div>
            </div>
        </div>
    );
}
